<template>
  <div class="location-code-guide mb-1">
    <b-button
      v-b-toggle.location-code-guide
      variant="flat-primary"
      size="sm"
      class="d-flex align-items-center px-50"
    >
      <feather-icon icon="InfoIcon" size="15" class="mr-50" />
      <span>How {{ levelLabel.toLowerCase() }} codes work</span>
      <feather-icon :icon="isOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="15" class="ml-50" />
    </b-button>

    <b-collapse id="location-code-guide" v-model="isOpen">
      <b-card no-body class="border mt-50 mb-0">
        <div class="p-1">
          <!-- Where this level sits, and what a full code looks like end to end -->
          <div v-for="chain in chains" :key="chain.key" class="mb-1">
            <h6 class="text-muted font-small-3 mb-50">{{ chain.title }}</h6>

            <div class="d-flex flex-wrap align-items-center">
              <template v-for="(step, index) in chain.steps">
                <div :key="`${chain.key}-${step.level}`" class="mr-50 mb-50 text-center">
                  <b-badge :variant="step.level === level ? 'primary' : 'light-secondary'">
                    {{ step.label }}
                  </b-badge>
                  <div class="font-small-2 mt-25">
                    <code>{{ step.example }}</code>
                  </div>
                </div>

                <feather-icon
                  v-if="index < chain.steps.length - 1"
                  :key="`${chain.key}-${step.level}-arrow`"
                  icon="ChevronRightIcon"
                  size="14"
                  class="text-muted mr-50 mb-1"
                />
              </template>
            </div>

            <small v-if="chain.note" class="text-muted">{{ chain.note }}</small>
          </div>

          <!-- What that means on this page in particular -->
          <h6 class="text-muted font-small-3 mb-50">On this page</h6>
          <p class="mb-50">{{ guidance.headline }}</p>
          <ul class="pl-2 mb-1">
            <li v-for="(point, index) in guidance.points" :key="index" class="font-small-3">
              {{ point }}
            </li>
          </ul>

          <h6 class="text-muted font-small-3 mb-50">Good to know</h6>
          <ul class="pl-2 mb-0">
            <li v-for="(point, index) in sharedPoints" :key="index" class="font-small-3">
              {{ point }}
            </li>
          </ul>
        </div>
      </b-card>
    </b-collapse>
  </div>
</template>

<script>
import { BButton, BCard, BBadge, BCollapse, VBToggle } from 'bootstrap-vue'

// The two code chains in the system. Each level's code is its parent's code plus
// the part typed on that level's form, so a full assembly code carries every
// ancestor with it.
const DOMESTIC_CHAIN = {
  key: 'domestic',
  title: 'Ghana',
  steps: [
    { level: 'region', label: 'Region', example: 'GH03' },
    { level: 'area', label: 'Area', example: 'GH0304' },
    { level: 'district', label: 'District', example: 'GH030401' },
    { level: 'assembly', label: 'Assembly', example: 'GH03040101' },
  ],
}

const MISSION_CHAIN = {
  key: 'mission',
  title: 'Missions',
  steps: [
    { level: 'country', label: 'Country', example: 'TG' },
    { level: 'area', label: 'Area', example: 'TG01' },
    { level: 'district', label: 'District', example: 'TG0101' },
    { level: 'assembly', label: 'Assembly', example: 'TG010101' },
  ],
  note: 'Mission blocks and RMCs group countries administratively but carry no code of their own — a mission code starts at the country ISO code.',
}

const LEVELS = {
  region: {
    label: 'Region',
    chains: [DOMESTIC_CHAIN],
    headline: 'A region sits at the top of the Ghana chain, so nothing is filled in for you — you type the whole code.',
    points: [
      'The house style is the Ghana ISO code followed by the region number, e.g. GH03.',
      'Every area, district and assembly under this region is seeded from what you save here.',
      'Changing a region code later does not rewrite the codes already saved beneath it — those have to be edited by hand.',
    ],
  },
  country: {
    label: 'Country',
    chains: [MISSION_CHAIN],
    headline: "A country's ISO code is the root of every mission-side code, which is why it is required.",
    points: [
      'Use the two-letter ISO code for the country, e.g. TG for Togo.',
      'Areas created under this country are seeded with it, so the districts and assemblies below them inherit it too.',
      'Countries are grouped into RMCs and mission blocks for reporting; neither adds anything to the code.',
    ],
  },
  area: {
    label: 'Area',
    chains: [DOMESTIC_CHAIN, MISSION_CHAIN],
    headline: 'An area hangs off either a region (Ghana) or a country (missions), and the "Belongs To" choice decides which code seeds it.',
    points: [
      "Pick the parent first. The Code field is then pre-filled with the parent region's code or the country's ISO code — type only this area's own part after it.",
      'Switching to a different parent re-seeds the prefix and carries over the part you typed.',
      'If the parent has no code saved, there is no prefix to apply and the field is left empty.',
    ],
  },
  district: {
    label: 'District',
    chains: [DOMESTIC_CHAIN, MISSION_CHAIN],
    headline: 'A district always belongs to an area, and its code starts with that area\'s code.',
    points: [
      "Pick the area first. The Code field is then pre-filled with the area's code — type only this district's own part after it.",
      'Switching to a different area re-seeds the prefix and carries over the part you typed.',
      'If the area has no code saved, there is no prefix to apply and the field is left empty.',
    ],
  },
  assembly: {
    label: 'Assembly',
    chains: [DOMESTIC_CHAIN, MISSION_CHAIN],
    headline: 'An assembly always belongs to a district, and its code starts with that district\'s code — the longest code in the chain.',
    points: [
      "Pick the district first. The Code field is then pre-filled with the district's code — type only this assembly's own part after it.",
      'Switching to a different district re-seeds the prefix and carries over the part you typed.',
      'If the district has no code saved, there is no prefix to apply and the field is left empty.',
    ],
  },
}

// True of every level, so it is worth saying once rather than in each block above.
const SHARED_POINTS = [
  'A code typed from scratch is treated as yours: once you write over the seeded prefix, the form stops managing the field.',
  'The prefix is a convention the form helps you follow, not a rule the system enforces — codes are optional and are not checked for uniqueness.',
  'Search matches names, not codes. To line a family up, use Sort by → Code, which puts children directly under their parent.',
]

export default {
  name: 'LocationCodeGuide',
  components: {
    BCard,
    BBadge,
    BButton,
    BCollapse,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  props: {
    // Which page this guide is rendered on: region, area, district, assembly or country.
    level: {
      type: String,
      required: true,
      validator: value => Object.keys(LEVELS).includes(value),
    },
  },
  data() {
    return {
      isOpen: false,
      sharedPoints: SHARED_POINTS,
    }
  },
  computed: {
    guidance() {
      return LEVELS[this.level]
    },
    levelLabel() {
      return this.guidance.label
    },
    chains() {
      return this.guidance.chains
    },
  },
}
</script>
